import NON_TRAVELING_PARAM_NAMES from './constants'

/**
 * Merge the current URL's query string into a navigation target (PPI-1770).
 *
 * Marketing params (`utm_*`, `gclid`, `src`, …) only ever existed on the funnel's
 * entry URL, because every step navigation assigned a hardcoded route:
 * `window.location = QUOTES`. Every page after entry therefore had a bare URL and
 * every analytics event fired from it carried no campaign. Putting the params back
 * in the URL fixes every consumer at once, because they all read the URL.
 *
 * Params merge additively and the **target wins**: `application/index.ts` rebuilds
 * `planid`/`period`/`policies` from state, and those values must survive intact
 * rather than be overwritten by whatever was inherited from the previous page.
 *
 * @param target Route to navigate to, optionally with its own query and hash.
 * @param currentSearch Current query string; defaults to the live URL.
 * @returns The target with the surviving params merged in, and no stray `?`.
 */
export default function preserveMarketingParams(
  target: string,
  currentSearch: string = window.location.search
): string {
  // One caller passes `e.currentTarget.href`, and not every element bound to that
  // handler is an anchor — `#header-start-chat` may have no href at all. Hand a
  // falsy target straight back so those clicks behave exactly as they do today
  // instead of throwing here.
  if (!target) return target

  const hashIndex = target.indexOf('#')
  const hash = hashIndex === -1 ? '' : target.slice(hashIndex)
  const withoutHash = hashIndex === -1 ? target : target.slice(0, hashIndex)

  const queryIndex = withoutHash.indexOf('?')
  const path = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
  const targetParams = new URLSearchParams(
    queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1)
  )

  const merged = new URLSearchParams(currentSearch)
  NON_TRAVELING_PARAM_NAMES.forEach(name => merged.delete(name))
  // Clear inherited values for the keys the target sets, then append the
  // target's own, so a target that repeats a key keeps every one of its values.
  Array.from(new Set(Array.from(targetParams.keys()))).forEach(key => merged.delete(key))
  targetParams.forEach((value, key) => merged.append(key, value))

  const query = merged.toString()
  return query ? `${path}?${query}${hash}` : `${path}${hash}`
}
